import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useDropzone } from 'react-dropzone';

import { logError } from '../../lib/logger';
import { getMetaContent } from '../../lib/meta';
import { applyUserFlexiconImage, uploadUserFlexicon } from '../../lib/user_flexicon_upload';
import { cn } from '../../lib/utils';

import Flexicon from '../shared/Flexicon';

const FLEXICON_IMAGE_CONTENT_TYPES = JSON.parse(getMetaContent('channel:flexicon_image_content_types') || '[]');
const DROPZONE_ACCEPT = FLEXICON_IMAGE_CONTENT_TYPES.reduce(function reduceAcceptCallback(accept, type) {
  accept[type] = [];
  return accept;
}, {});

function readableErrorFor(error) {
  if (error?.validations?.content_length) return 'Image is too large.';
  if (error?.validations?.content_type) return 'Image type is not supported.';
  if (error?.message === 'upload-processing-timeout') return 'Upload took too long. Try a smaller image.';
  return 'Could not upload the image. Please try again.';
}

function serverFlexicon(attachment) {
  return {
    kind: 'image',
    image: { id: attachment.id, variants: attachment.variants },
  };
}

function pickImageFile(files) {
  if (!files?.length) return null;
  if (!FLEXICON_IMAGE_CONTENT_TYPES.length) return files[0];
  return files.find(function findImageFileCallback(file) {
    return FLEXICON_IMAGE_CONTENT_TYPES.includes(file.type);
  }) || null;
}

function getClipboardFiles(event) {
  const files = Array.from(event.clipboardData?.files || []);
  if (files.length) return files;
  return Array.from(event.clipboardData?.items || [])
    .filter(function filterFileItemsCallback(item) {
      return item.kind === 'file';
    })
    .map(function mapItemToFileCallback(item) {
      return item.getAsFile();
    })
    .filter(Boolean);
}

function isEditableElement(element) {
  if (!(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) return true;
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)) return true;
  return element.getAttribute('role') === 'textbox';
}

function isAbortError(error) {
  return error?.message === 'upload-aborted';
}

export default function AvatarUploader({ initialFlexicon, onErrorChange }) {
  const fileInputRef = useRef(null);
  // The AbortController for the in-flight upload. Picking a new file (or unmounting)
  // aborts the previous one so its trailing state updates don't clobber the new upload.
  const inFlightControllerRef = useRef(null);
  const [flexicon, setFlexicon] = useState(initialFlexicon || null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const isBusy = Boolean(localPreviewUrl);

  // Revoke whichever object URL is currently in state when it's swapped out or on unmount.
  useEffect(
    function revokeLocalPreviewUrlEffect() {
      if (!localPreviewUrl) return undefined;
      return function revokeLocalPreviewUrlCleanup() {
        URL.revokeObjectURL(localPreviewUrl);
      };
    },
    [localPreviewUrl],
  );

  // Abort any in-flight upload when the component unmounts so we don't keep polling or
  // PATCH /me with a flexicon the user can no longer see.
  useEffect(function abortInFlightOnUnmountEffect() {
    return function abortInFlightCleanup() {
      inFlightControllerRef.current?.abort();
      inFlightControllerRef.current = null;
    };
  }, []);

  const reportError = useCallback(
    function reportErrorCallback(message) {
      onErrorChange?.(message);
    },
    [onErrorChange],
  );

  const handleUploadProgress = useCallback(function handleUploadProgressCallback(percent) {
    setProgress(percent);
  }, []);

  const handleProcessingStart = useCallback(function handleProcessingStartCallback() {
    setIsProcessing(true);
  }, []);

  const handleFile = useCallback(
    async function handleFileCallback(file) {
      if (!file) return;
      if (FLEXICON_IMAGE_CONTENT_TYPES.length > 0 && !FLEXICON_IMAGE_CONTENT_TYPES.includes(file.type)) {
        reportError('That file type is not supported.');
        return;
      }

      inFlightControllerRef.current?.abort();
      const controller = new AbortController();
      inFlightControllerRef.current = controller;

      reportError(null);
      setIsProcessing(false);
      setProgress(0);
      setLocalPreviewUrl(URL.createObjectURL(file));

      try {
        const attachment = await uploadUserFlexicon(file, {
          onProgress: handleUploadProgress,
          onProcessing: handleProcessingStart,
          signal: controller.signal,
        });
        await applyUserFlexiconImage(attachment.id, { signal: controller.signal });
        if (controller.signal.aborted) return;
        setFlexicon(serverFlexicon(attachment));
      } catch (error) {
        if (isAbortError(error) || controller.signal.aborted) return;
        logError('AvatarUploader', 'Failed to upload user flexicon', { error });
        reportError(readableErrorFor(error));
      } finally {
        if (inFlightControllerRef.current === controller) {
          inFlightControllerRef.current = null;
          setLocalPreviewUrl(null);
          setIsProcessing(false);
          setProgress(0);
        }
      }
    },
    [handleProcessingStart, handleUploadProgress, reportError],
  );

  const handlePickClick = useCallback(function handlePickClickCallback() {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    function handleFileInputChangeCallback(event) {
      const file = event.target.files?.[0];
      if (fileInputRef.current) fileInputRef.current.value = '';
      handleFile(file);
    },
    [handleFile],
  );

  const handleDrop = useCallback(
    function handleDropCallback(acceptedFiles) {
      handleFile(pickImageFile(acceptedFiles));
    },
    [handleFile],
  );

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    disabled: isBusy,
    accept: DROPZONE_ACCEPT,
    onDrop: handleDrop,
  });

  // Paste-from-clipboard support — handle anywhere on the page, but defer to the focused
  // text field when the clipboard payload is mixed with plain text (the user is
  // probably pasting text, not an avatar).
  useEffect(
    function pasteListenerEffect() {
      if (isBusy) return undefined;

      function handlePasteEvent(event) {
        if (event.defaultPrevented) return;
        const files = getClipboardFiles(event);
        if (!files.length) return;

        const image = pickImageFile(files);
        if (!image) return;

        if (isEditableElement(document.activeElement) && event.clipboardData?.types?.includes('text/plain')) return;

        event.preventDefault();
        handleFile(image);
      }

      document.addEventListener('paste', handlePasteEvent);
      return function pasteListenerCleanup() {
        document.removeEventListener('paste', handlePasteEvent);
      };
    },
    [handleFile, isBusy],
  );

  let helperLabel;
  if (isProcessing) helperLabel = 'Processing…';
  else if (isBusy) helperLabel = `Uploading… ${progress}%`;
  else helperLabel = 'Add photo';

  // Local previews come from the raw picked file. Render them with object-cover so the
  // visible crop matches the server's square_medium variant (Lambda center-crops to 1:1);
  // otherwise the swap from preview → server image causes a slight visual jump.
  return (
    <div className="flex flex-col items-center gap-5">
      <button
        {...getRootProps()}
        type="button"
        onClick={handlePickClick}
        disabled={isBusy}
        className={cn(
          'relative inline-flex size-30 aspect-square cursor-pointer items-center justify-center overflow-hidden rounded-full border border-black/5 disabled:cursor-wait',
          isDragActive && 'ring-2 ring-black/20',
        )}
        data-app-region="none"
        aria-label={helperLabel}
      >
        {localPreviewUrl ? (
          <img src={localPreviewUrl} alt="" className="size-full object-cover" draggable="false" />
        ) : (
          <Flexicon resource={{ flexicon }} size="size-30" />
        )}
        {isBusy && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/40 text-sm font-medium text-white">
            {isProcessing ? '…' : `${progress}%`}
          </span>
        )}
      </button>

      <button
        type="button"
        onClick={handlePickClick}
        disabled={isBusy}
        className="cursor-pointer text-sm leading-[22px] font-medium text-idle-blue hover:underline disabled:cursor-wait disabled:opacity-70 disabled:hover:no-underline"
        data-app-region="none"
      >
        {helperLabel}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept={FLEXICON_IMAGE_CONTENT_TYPES.join(',')}
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
}
